import { useCallback, useRef, useState } from "react";
import { DeviceEventEmitter } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Toast from "react-native-toast-message";

import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import { addRoom } from "@/components/http/events";
import { t, tc, tn } from "@/localization";
import { ROOMS_CHANGED } from "./useRoomGroup";
import { forNoun, roomLabelText, subscriptionActive } from "./rooms";
import { addsCameras } from "./multicam";

const INTRO_SEEN_KEY = "rooms_intro_seen";

// What the toasts say about the new room, for each noun: the English is the
// same, a translation agrees with the noun ("Desbloquéala", "Desbloquéalo").
const ADDED_WORDS = {
  Room: () => ({
    unlock: tc("Room", "Unlock it to stream from it."),
    failed: tc("Room", "Could not add the room"),
  }),
  Stage: () => ({
    unlock: tc("Stage", "Unlock it to stream from it."),
    failed: tc("Stage", "Could not add the room"),
  }),
  Track: () => ({
    unlock: tc("Track", "Unlock it to stream from it."),
    failed: tc("Track", "Could not add the room"),
  }),
  Court: () => ({
    unlock: tc("Court", "Unlock it to stream from it."),
    failed: tc("Court", "Could not add the room"),
  }),
  Field: () => ({
    unlock: tc("Field", "Unlock it to stream from it."),
    failed: tc("Field", "Could not add the room"),
  }),
  Hall: () => ({
    unlock: tc("Hall", "Unlock it to stream from it."),
    failed: tc("Hall", "Could not add the room"),
  }),
  Camera: () => ({
    unlock: tc("Camera", "Unlock it to stream from it."),
    failed: tc("Camera", "Could not add the room"),
  }),
};

const errorMessage = (err) =>
  err?.errors?.[0]?.message || err?.message || t("Please try again.");

// Adding rooms. The explainer (Add a room, components/rooms/AddRoomPane.js) opens
// for an event's first room, when the noun is chosen, and until it has been seen
// once; after that "Add" adds right away. Personal accounts always see it: it
// says what the next camera costs. `showIntro(primary)` opens it: in the
// event's pane on wide layouts, as its own screen on phones.
const useAddRoom = ({ navigation, showIntro } = {}) => {
  // Teams: rooms are added in the account you work in (its plan and credits).
  const auth = useWorkspaceAuth();
  const [adding, setAdding] = useState(false);
  const busy = useRef(false);

  const add = useCallback(
    async (primary, noun, { openRoom, switchRoom } = {}) => {
      if (busy.current) return null;
      busy.current = true;
      setAdding(true);
      try {
        const room = await addRoom({ slug: primary.slug, room_noun: noun });
        try {
          await AsyncStorage.setItem(INTRO_SEEN_KEY, "1");
        } catch (e) {}
        await auth.refetchProfile?.();
        DeviceEventEmitter.emit(ROOMS_CHANGED, room);
        const days = Math.max(1, Number(room?.days) || 1);
        Toast.show({
          type: "success",
          text1: t("{label} added", {
            label: roomLabelText(room?.room_label) || tc("room noun", "Room"),
          }),
          text2: !room?.is_published
            ? forNoun(
                ADDED_WORDS,
                room?.room_noun || primary?.room_noun || noun
              ).unlock
            : subscriptionActive(auth)
            ? t("Included in your Unlimited plan.")
            : tn("{count} credit used.", "{count} credits used.", days),
        });
        if (openRoom && room?.slug) {
          // From an event's screen: switch to the new room in place.
          if (switchRoom) switchRoom(room);
          else if (navigation)
            navigation.push("EventEdit", { slug: room.slug, key: room.slug });
        }
        return room;
      } catch (err) {
        console.error(err);
        Toast.show({
          type: "error",
          text1: forNoun(ADDED_WORDS, primary?.room_noun || noun).failed,
          text2: errorMessage(err),
        });
        return null;
      } finally {
        busy.current = false;
        setAdding(false);
      }
    },
    [auth, navigation]
  );

  const openAddRoom = useCallback(
    async (primary, roomCount = 0, options = {}) => {
      if (!primary?.slug) return;
      let introSeen = false;
      try {
        introSeen = (await AsyncStorage.getItem(INTRO_SEEN_KEY)) === "1";
      } catch (e) {}
      if (
        !addsCameras(auth) &&
        introSeen &&
        primary.room_noun &&
        roomCount > 0
      ) {
        await add(primary, primary.room_noun, options);
        return;
      }
      showIntro?.(primary);
    },
    [add, auth, showIntro]
  );

  return { openAddRoom, add, adding };
};

export default useAddRoom;
